import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCheck, ThumbsUp, ThumbsDown, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentBehaviorPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [behaviors, setBehaviors] = useState([]);
  const [stats, setStats] = useState({ positive: 0, negative: 0, neutral: 0 });

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const response = await fetch(`${API_URL}/api/students/parent/children`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setChildren(data);
          if (data.length > 0) {
            setSelectedChild(data[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch children:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchChildren();
    }
  }, [token]);

  useEffect(() => {
    const fetchBehaviors = async () => {
      if (!selectedChild) return;
      
      try {
        const response = await fetch(`${API_URL}/api/behavior/student/${selectedChild}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          // API returns incidents array, not records
          const records = data.incidents || data.records || data || [];
          setBehaviors(records);
          
          // Use stats from API if available
          if (data.stats) {
            setStats({
              positive: data.stats.positive_count || 0,
              negative: data.stats.negative_count || 0,
              neutral: data.stats.total_incidents - (data.stats.positive_count || 0) - (data.stats.negative_count || 0),
              total_points: data.stats.total_points || 0,
              grade: data.stats.grade || 'N/A'
            });
          } else {
            // Calculate stats from records
            const positive = records.filter(r => r.incident_type === 'positive' || r.type === 'positive' || r.points > 0).length;
            const negative = records.filter(r => r.incident_type === 'negative' || r.type === 'negative' || r.points < 0).length;
            const neutral = records.length - positive - negative;
            setStats({ positive, negative, neutral, total_points: 0, grade: 'N/A' });
          }
        }
      } catch (error) {
        console.error('Failed to fetch behaviors:', error);
        setBehaviors([]);
      }
    };

    if (selectedChild && token) {
      fetchBehaviors();
    }
  }, [selectedChild, token]);

  const getBehaviorIcon = (type, points, incident_type) => {
    const behaviorType = incident_type || type;
    if (behaviorType === 'positive' || points > 0) return <ThumbsUp className="h-5 w-5 text-green-500" />;
    if (behaviorType === 'negative' || points < 0) return <ThumbsDown className="h-5 w-5 text-red-500" />;
    return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
  };

  const getBehaviorBadge = (type, points, incident_type) => {
    const behaviorType = incident_type || type;
    if (behaviorType === 'positive' || points > 0) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (behaviorType === 'negative' || points < 0) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  if (loading) {
    return (
      <DashboardLayout title="Child Behavior">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const selectedChildData = children.find(c => c.id === selectedChild);

  return (
    <DashboardLayout title="Child Behavior">
      <div className="space-y-6">
        {/* Child Selector */}
        {children.length > 1 && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium">Select Child:</label>
                <Select value={selectedChild} onValueChange={setSelectedChild}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Points</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.total_points || 0}</p>
                  </div>
                  <UserCheck className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Grade</p>
                    <p className="text-2xl font-bold text-purple-600">{stats.grade || 'N/A'}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Positive</p>
                    <p className="text-2xl font-bold text-green-600">{stats.positive}</p>
                  </div>
                  <ThumbsUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Negative</p>
                    <p className="text-2xl font-bold text-red-600">{stats.negative}</p>
                  </div>
                  <ThumbsDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Neutral</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.neutral || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Behavior Records */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Behavior Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            {behaviors.length === 0 ? (
              <div className="text-center py-12">
                <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No behavior records found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Behavior records from teachers will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {behaviors.map((record, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border rounded-lg p-4"
                    data-testid={`behavior-record-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      {getBehaviorIcon(record.type, record.points, record.incident_type)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">{record.title || record.category_name || record.category || 'Behavior Record'}</h4>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBehaviorBadge(record.type, record.points, record.incident_type)}`}>
                            {record.points > 0 ? `+${record.points}` : record.points} points
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{record.description || record.notes || 'No description'}</p>
                        {record.category_name && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                            {record.category_name}
                          </span>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Date: {record.incident_date || record.date || (record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A')}</span>
                          {record.recorded_by && <span>By: {record.recorded_by}</span>}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ParentBehaviorPage;
